import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { getPricingForCountrySync, formatCurrency, getPaymentAmount, initializeExchangeRates, BACKEND_URL } from './constants';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAuth } from "./AuthContext";
import { getSpecialtyPricingInfo } from './constants';


// Custom SVG Icons (replacing lucide-react)
const ChevronLeft = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polyline points="15,18 9,12 15,6"></polyline>
  </svg>
);

const TrendingUp = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
    <polyline points="17,6 23,6 23,12"></polyline>
  </svg>
);

const Lock = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const Star = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
  </svg>
);

const Heart = ({ size = 24, className = "", filled = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const Users = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const Globe = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const ExternalLink = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
    <polyline points="15,3 21,3 21,9"></polyline>
    <line x1="10" y1="14" x2="21" y2="3"></line>
  </svg>
);

const MapPin = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const Building = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
    <path d="M6 12h4v4H6v-4Z"></path>
    <path d="M14 12h4v4h-4v-4Z"></path>
    <path d="M6 20h4v4H6v-4Z"></path>
    <path d="M14 20h4v4h-4v-4Z"></path>
  </svg>
);

// Custom CSS-based Charts (monochrome)
const RadialProgress = ({ percentage, size = 200, isPaid, onUpgradeClick }) => {
  const radius = size / 2 - 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e5e7eb" strokeWidth="12" fill="none" />
        {isPaid && (
          <circle cx={size / 2} cy={size / 2} r={radius} stroke="#171717" strokeWidth="12" fill="none" strokeDasharray={strokeDasharray} strokeDashoffset={strokeDashoffset} strokeLinecap="round" className="transition-all duration-1000 ease-out" />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          {isPaid ? (
            <>
              <div className="text-3xl font-bold text-black dark:text-white tracking-[-0.03em]">{percentage.toFixed(1)}%</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">Compatible</div>
            </>
          ) : (
            <button onClick={onUpgradeClick} className="flex flex-col items-center group">
              <div className="w-12 h-12 bg-black dark:bg-gray-600 rounded-2xl flex items-center justify-center mb-2 group-hover:scale-105 transition-transform duration-200">
                <Lock size={20} className="text-white" />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium group-hover:text-black dark:group-hover:text-white transition-colors">Unlock Chart</div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PieChart = ({ data, size = 250, isPaid, onUpgradeClick }) => {
  let cumulativePercentage = 0;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {isPaid ? (
            data.map((item, index) => {
              const percentage = item.value;
              const radius = (size / 2) - 20;
              const circumference = 2 * Math.PI * radius;
              cumulativePercentage += percentage;
              return (
                <circle key={index} cx={size / 2} cy={size / 2} r={radius} stroke={item.color} strokeWidth="20" fill="none" strokeDasharray={`${(percentage / 100) * circumference} ${circumference}`} strokeDashoffset={-((cumulativePercentage - percentage) / 100) * circumference} strokeLinecap="round" className="transition-all duration-1000 ease-out" style={{ transformOrigin: `${size / 2}px ${size / 2}px` }} />
              );
            })
          ) : (
            <circle cx={size / 2} cy={size / 2} r={(size / 2) - 20} stroke="#e5e7eb" strokeWidth="20" fill="none" className="opacity-50" />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            {isPaid ? (
              <>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">Program</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Composition</div>
              </>
            ) : (
              <button onClick={onUpgradeClick} className="flex flex-col items-center group">
                <div className="w-12 h-12 bg-black dark:bg-gray-600 rounded-2xl flex items-center justify-center mb-2 group-hover:scale-105 transition-transform duration-200">
                  <Lock size={20} className="text-white" />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium group-hover:text-black dark:group-hover:text-white transition-colors">Unlock Chart</div>
              </button>
            )}
          </div>
        </div>
      </div>
      {isPaid && (
        <div className="mt-5 space-y-2">
          {data.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{item.value}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function ComparePage() {
  const { state } = useLocation();
  const { collegeData, specialty, programNameKey } = state || {};
  const navigate = useNavigate();

  const [matchPercentage, setMatchPercentage] = useState(null);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isAddingFavorite, setIsAddingFavorite] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedPaymentCountry, setSelectedPaymentCountry] = useState("India");
  const [showCurrencySelector, setShowCurrencySelector] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { user, userProfile, refreshUserData, hasSpecialtyAccess, addSpecialtyAccess } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const hasCurrentSpecialtyAccess = hasSpecialtyAccess(specialty);

  // Resolve program name from collegeData (TablePage passes programNameKey when navigating from table)
  const getDisplayProgramName = (data, preferredKey) => {
    if (!data || typeof data !== 'object') return 'Program Name Not Available';
    // Use the exact column key the table used (e.g. "NAME", "Program Name", etc.)
    if (preferredKey && data[preferredKey] != null && String(data[preferredKey]).trim())
      return String(data[preferredKey]).trim();
    // Try well-known multi-word keys first
    const knownKeys = ['Program Name', 'PROGRAM NAME', 'Program name', 'program name', 'Program_Name', 'PROGRAM_NAME'];
    for (const k of knownKeys) {
      const v = data[k];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    // Try any key matching "program name" pattern
    const matchedKey = Object.keys(data).find(
      (key) => /program\s*name/i.test(key) && data[key] != null && String(data[key]).trim()
    );
    if (matchedKey) return String(data[matchedKey]).trim();
    // Try exact key "NAME" – used by Internal Medicine 2026 and similar datasets
    if (data['NAME'] != null && String(data['NAME']).trim()) return String(data['NAME']).trim();
    if (data['Name'] != null && String(data['Name']).trim()) return String(data['Name']).trim();
    return 'Program Name Not Available';
  };

  const displayProgramName = collegeData ? getDisplayProgramName(collegeData, programNameKey) : 'Program Name Not Available';
  const programName = collegeData?.['Program Name'];
  const programNameAlt = collegeData?.['PROGRAM NAME'];

  const cleanPercentage = (value) => {
    if (!value) return 0;
    const cleanValue = value.toString().replace('%', '');
    return parseFloat(cleanValue) || 0;
  };

  const displayPercentage = (value) => {
    if (!value) return '—';
    const str = value.toString();
    return str.includes('%') ? str : `${str}%`;
  };

  const getPopularCountriesForPayment = () => [
    { country: "India", currency: "INR", flag: "🇮🇳" },
    { country: "United States", currency: "USD", flag: "🇺🇸" },
    { country: "United Kingdom", currency: "GBP", flag: "🇬🇧" },
    { country: "Canada", currency: "CAD", flag: "🇨🇦" },
    { country: "Australia", currency: "AUD", flag: "🇦🇺" },
    { country: "Germany", currency: "EUR", flag: "🇩🇪" },
    { country: "France", currency: "EUR", flag: "🇫🇷" },
    { country: "Spain", currency: "EUR", flag: "🇪🇸" },
    { country: "Italy", currency: "EUR", flag: "🇮🇹" },
    { country: "Netherlands", currency: "EUR", flag: "🇳🇱" },
    { country: "Switzerland", currency: "CHF", flag: "🇨🇭" },
    { country: "Sweden", currency: "SEK", flag: "🇸🇪" },
    { country: "Norway", currency: "NOK", flag: "🇳🇴" },
    { country: "Denmark", currency: "DKK", flag: "🇩🇰" },
    { country: "Singapore", currency: "SGD", flag: "🇸🇬" },
    { country: "Malaysia", currency: "MYR", flag: "🇲🇾" },
    { country: "Thailand", currency: "THB", flag: "🇹🇭" },
    { country: "Philippines", currency: "PHP", flag: "🇵🇭" },
    { country: "Indonesia", currency: "IDR", flag: "🇮🇩" },
    { country: "Vietnam", currency: "VND", flag: "🇻🇳" },
    { country: "Japan", currency: "JPY", flag: "🇯🇵" },
    { country: "South Korea", currency: "KRW", flag: "🇰🇷" },
    { country: "China", currency: "CNY", flag: "🇨🇳" },
    { country: "Hong Kong", currency: "HKD", flag: "🇭🇰" },
    { country: "Taiwan", currency: "TWD", flag: "🇹🇼" },
    { country: "Pakistan", currency: "PKR", flag: "🇵🇰" },
    { country: "Bangladesh", currency: "BDT", flag: "🇧🇩" },
    { country: "Sri Lanka", currency: "LKR", flag: "🇱🇰" },
    { country: "Nepal", currency: "NPR", flag: "🇳🇵" },
    { country: "Saudi Arabia", currency: "SAR", flag: "🇸🇦" },
    { country: "United Arab Emirates", currency: "AED", flag: "🇦🇪" },
    { country: "Qatar", currency: "QAR", flag: "🇶🇦" },
    { country: "Kuwait", currency: "KWD", flag: "🇰🇼" },
    { country: "Bahrain", currency: "BHD", flag: "🇧🇭" },
    { country: "Oman", currency: "OMR", flag: "🇴🇲" },
    { country: "Jordan", currency: "JOD", flag: "🇯🇴" },
    { country: "Israel", currency: "ILS", flag: "🇮🇱" },
    { country: "South Africa", currency: "ZAR", flag: "🇿🇦" },
    { country: "Nigeria", currency: "NGN", flag: "🇳🇬" },
    { country: "Kenya", currency: "KES", flag: "🇰🇪" },
    { country: "Egypt", currency: "EGP", flag: "🇪🇬" },
    { country: "Brazil", currency: "BRL", flag: "🇧🇷" },
    { country: "Mexico", currency: "MXN", flag: "🇲🇽" },
    { country: "Poland", currency: "PLN", flag: "🇵🇱" },
    { country: "Russia", currency: "RUB", flag: "🇷🇺" },
    { country: "Turkey", currency: "TRY", flag: "🇹🇷" },
  ];

  const extractWebsiteUrl = (collegeData) => {
    const websiteColumns = [
      'RESIDENTS/PROGRAM WEBSITE', 'RESIDENTS/ PROGRAM WEBSITE',
      'WEBSITE', 'Website', 'website',
      'RESIDENTS', 'Residents',
      'Additional Information', 'Additional info'
    ];
    for (const column of websiteColumns) {
      const value = collegeData[column];
      if (value && value !== '—' && value !== 'N/A') {
        if (value.includes('http')) return value;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = value.match(urlRegex);
        if (matches && matches.length > 0) return matches[0];
        if (value.includes('.') && !value.includes(' ')) return `https://${value}`;
      }
    }
    return null;
  };

  const getAggregateMarksDisplay = (userProfile) => {
    if (userProfile?.obtainedMarks && userProfile?.maximumMarks) {
      const percentage = userProfile?.aggregatePercentage || ((parseFloat(userProfile.obtainedMarks) / parseFloat(userProfile.maximumMarks)) * 100).toFixed(2);
      return `${userProfile.obtainedMarks}/${userProfile.maximumMarks} (${percentage}%)`;
    }
    return '—';
  };

  const calculateNewCompatibility = useCallback((collegeData, userId) => {
    const nonUSImgPercent = cleanPercentage(collegeData['%NON US IMGS'] || collegeData['% NON US IMGS'] || collegeData['%non-US IMG'] || collegeData['% non-US IMG'] || 0);
    const positions = parseInt(collegeData['POSITIONS'] || 0);
    if (nonUSImgPercent === 0 || positions === 0) {
      const seedString = `${userId}_${collegeData['Program Name'] || collegeData['PROGRAM NAME'] || ''}_nodata`;
      let hash = 0;
      for (let i = 0; i < seedString.length; i++) {
        const char = seedString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      const variation = ((Math.abs(hash) % 1000) / 1000) * 2;
      return Math.min(Math.max(3 + variation, 3), 5);
    }
    const baseScore = (nonUSImgPercent / 100) * positions;
    const seedString = `${userId}_${collegeData['Program Name'] || collegeData['PROGRAM NAME'] || ''}`;
    let hash = 0;
    for (let i = 0; i < seedString.length; i++) {
      const char = seedString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    const randomFactor = ((Math.abs(hash) % 1000) / 1000) * 10 - 5;
    const finalScore = baseScore * (1 + randomFactor / 100);
    let scaledScore;
    if (finalScore <= 1) scaledScore = 3 + (finalScore * 12);
    else if (finalScore <= 10) scaledScore = 15 + ((finalScore - 1) / 9) * 45;
    else scaledScore = 60 + Math.min(((finalScore - 10) / 40) * 30, 30);
    return Math.min(Math.max(scaledScore, 3), 90);
  }, []);

  const checkFavoriteStatus = useCallback(async () => {
    if (!auth.currentUser || !collegeData) {
      setIsFavorite(false);
      return;
    }
    const resolved = getDisplayProgramName(collegeData);
    const programName = resolved === 'Program Name Not Available' ? '' : resolved;
    const currentSpecialty = specialty || collegeData.specialty || collegeData.Specialty || "Unknown";
    try {
      const favoritesRef = doc(db, "favorites", auth.currentUser.uid);
      const favoritesSnap = await getDoc(favoritesRef);
      if (!favoritesSnap.exists()) {
        setIsFavorite(false);
        return;
      }
      const favoritesData = favoritesSnap.data();
      const specialtyFavorites = favoritesData[currentSpecialty] || [];
      if (specialtyFavorites.length === 0) {
        setIsFavorite(false);
        return;
      }
      const isCurrentlyFavorite = specialtyFavorites.some(fav => {
        const favName = (fav['Program Name'] || fav['PROGRAM NAME'] || '').trim();
        return favName === programName || favName.toLowerCase() === programName.toLowerCase();
      });
      setIsFavorite(isCurrentlyFavorite);
    } catch (error) {
      console.error("Error in checkFavoriteStatus:", error);
      setIsFavorite(false);
    }
  }, [collegeData, specialty]);

  const toggleFavorite = async () => {
    if (!auth.currentUser || !collegeData || isAddingFavorite) return;
    const currentSpecialty = specialty || collegeData.specialty || collegeData.Specialty;
    if (!currentSpecialty) {
      alert("Cannot add to favorites: Specialty information is missing. Please try navigating to this program from a specialty page.");
      return;
    }
    setIsAddingFavorite(true);
    try {
      const favoritesRef = doc(db, "favorites", auth.currentUser.uid);
      const favoritesSnap = await getDoc(favoritesRef);
      const resolved = getDisplayProgramName(collegeData);
      const programName = resolved === 'Program Name Not Available' ? '' : resolved;
      const favoriteItem = { ...collegeData, specialty: currentSpecialty, addedAt: new Date().toISOString(), matchPercentage: matchPercentage || 0 };
      const cleanedFavoriteItem = {};
      Object.keys(favoriteItem).forEach(key => {
        const value = favoriteItem[key];
        if (value !== null && value !== undefined && value !== '' && value !== '—') cleanedFavoriteItem[key] = value;
      });
      if (favoritesSnap.exists()) {
        const currentData = favoritesSnap.data();
        const specialtyFavorites = currentData[currentSpecialty] || [];
        const isCurrentlyFavorite = specialtyFavorites.some(fav => {
          const favName = (fav['Program Name'] || fav['PROGRAM NAME'] || '').trim();
          return (favName === programName || favName.toLowerCase() === programName.toLowerCase());
        });
        if (isCurrentlyFavorite) {
          const updatedFavorites = specialtyFavorites.filter(fav => {
            const favName = (fav['Program Name'] || fav['PROGRAM NAME'] || '').trim();
            return favName !== programName && favName.toLowerCase() !== programName.toLowerCase();
          });
          await updateDoc(favoritesRef, { [currentSpecialty]: updatedFavorites, updatedAt: new Date().toISOString() });
          setIsFavorite(false);
        } else {
          const updatedFavorites = [...specialtyFavorites, cleanedFavoriteItem];
          await updateDoc(favoritesRef, { [currentSpecialty]: updatedFavorites, updatedAt: new Date().toISOString() });
          setIsFavorite(true);
        }
      } else {
        if (!isFavorite) {
          await setDoc(favoritesRef, { [currentSpecialty]: [cleanedFavoriteItem], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
          setIsFavorite(true);
        }
      }
      alert(`Program ${isFavorite ? "removed from" : "added to"} favorites successfully!`);
    } catch (error) {
      console.error("Error toggling favorite:", error);
      alert(`Failed to update favorites: ${error.message}`);
    } finally {
      setIsAddingFavorite(false);
    }
  };

  const getPricingInfo = () => {
    try {
      const pricingData = getSpecialtyPricingInfo(specialty, selectedPaymentCountry);
      const currencySymbol = getSymbolFromCurrency(pricingData.currency) || "$";
      const razorpayAmount = getPaymentAmount(pricingData.amount, pricingData.currency);
      return { amount: pricingData.amount, currency: pricingData.currency, razorpayAmount, currencySymbol, formattedAmount: formatCurrency(pricingData.amount, pricingData.currency) };
    } catch (error) {
      console.error('Error getting pricing info:', error);
      return { amount: 10, currency: "USD", razorpayAmount: 1000, currencySymbol: "$", formattedAmount: "$10.00" };
    }
  };

  const handlePayment = async () => {
    if (paymentLoading) return;
    setPaymentLoading(true);
    const res = await loadRazorpayScript();
    if (!res) {
      alert("Payment system failed to load. Please check your internet connection.");
      setPaymentLoading(false);
      return;
    }
    const { amount, currency, razorpayAmount, currencySymbol } = getPricingInfo();
    try {
      const keyResponse = await fetch(`${BACKEND_URL}/api/razorpay-key`);
      if (!keyResponse.ok) throw new Error('Failed to fetch Razorpay key');
      const { key: razorpayKey } = await keyResponse.json();
      const orderResponse = await fetch(`${BACKEND_URL}/api/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: razorpayAmount, currency, specialty, userId: auth.currentUser?.uid })
      });
      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(`Failed to create order: ${orderResponse.status} - ${errorText}`);
      }
      const order = await orderResponse.json();
      if (!order.id) throw new Error('Invalid order response: missing order ID');
      const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "Residency Companion",
        description: "Unlock Premium Features",
        modal: { backdrop_close: false, escape: true, animation: true, confirm_close: true, ondismiss: function() { setPaymentLoading(false); } },
        handler: async function(response) {
          try {
            const verificationResponse = await fetch(`${BACKEND_URL}/api/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature })
            });
            const verificationResult = await verificationResponse.json();
            if (verificationResult.success) {
              const uid = auth.currentUser?.uid;
              if (!uid) { setPaymentLoading(false); return; }
              await addSpecialtyAccess(specialty, { paymentId: response.razorpay_payment_id, amount, currency, country: selectedPaymentCountry });
              await refreshUserData(true);
              setShowPaymentModal(false);
              setPaymentLoading(false);
              alert(`Payment successful! ${specialty} programs unlocked for ${currencySymbol}${amount}.`);
            } else throw new Error('Payment verification failed');
          } catch (err) {
            console.error("Error updating payment status:", err);
            setPaymentLoading(false);
            alert("Payment processed but failed to update account. Please contact support.");
          }
        },
        prefill: { name: auth.currentUser?.displayName || "User", email: auth.currentUser?.email },
        theme: { color: "#171717", backdrop_color: "rgba(0, 0, 0, 0.5)" },
        retry: { enabled: true, max_count: 3 },
        remember_customer: false,
        timeout: 300,
        error: function(error) {
          console.error('Razorpay error:', error);
          setPaymentLoading(false);
          let errorMessage = "Payment failed. Please try again.";
          if (error.code === 'BAD_REQUEST_ERROR') errorMessage = "Invalid payment details. Please check and try again.";
          else if (error.code === 'NETWORK_ERROR') errorMessage = "Network error. Please check your internet connection.";
          else if (error.code === 'SERVER_ERROR') errorMessage = "Server error. Please try again in a moment.";
          alert(errorMessage);
        }
      };
      setShowPaymentModal(false);
      setTimeout(() => { const paymentObject = new window.Razorpay(options); paymentObject.open(); }, 200);
    } catch (error) {
      console.error('Error in order creation:', error);
      setPaymentLoading(false);
      alert("Failed to create order. Please try again.");
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    const handleNavigation = () => { if (user) refreshUserData(true); };
    window.addEventListener('pageshow', handleNavigation);
    return () => window.removeEventListener('pageshow', handleNavigation);
  }, [user, refreshUserData]);

  useEffect(() => {
    if (collegeData && user) {
      setIsFavorite(false);
      setIsAddingFavorite(false);
      setMatchPercentage(null);
      setAnimatedPercentage(0);
      const compatibilityScore = calculateNewCompatibility(collegeData, user.uid);
      setMatchPercentage(compatibilityScore);
      const timeoutId = setTimeout(() => checkFavoriteStatus(), 50);
      return () => clearTimeout(timeoutId);
    } else {
      setIsFavorite(false);
      setMatchPercentage(null);
      setIsAddingFavorite(false);
      setAnimatedPercentage(0);
    }
  }, [programName, programNameAlt, user, collegeData, calculateNewCompatibility, checkFavoriteStatus]);

  useEffect(() => {
    setIsFavorite(false);
    setIsAddingFavorite(false);
  }, [programName, programNameAlt]);

  useEffect(() => {
    const initRates = async () => {
      try { await initializeExchangeRates(); } catch (error) { console.warn('Failed to initialize exchange rates:', error); }
    };
    initRates();
  }, []);

  useEffect(() => {
    if (matchPercentage !== null) {
      const timer = setInterval(() => {
        setAnimatedPercentage(prev => {
          const target = parseFloat(matchPercentage);
          const diff = target - prev;
          if (Math.abs(diff) < 0.5) { clearInterval(timer); return target; }
          return prev + diff * 0.1;
        });
      }, 50);
      return () => clearInterval(timer);
    }
  }, [matchPercentage]);

  useEffect(() => {
    return () => {
      setIsFavorite(false);
      setIsAddingFavorite(false);
      setMatchPercentage(null);
    };
  }, []);

  if (!collegeData) {
    return (
      <div className="min-h-screen bg-[#fafafa] dark:bg-gray-900 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
        <div className="text-center space-y-8 px-6">
          <div className="w-12 h-12 border-2 border-black/20 dark:border-gray-600 border-t-black dark:border-t-white rounded-full animate-spin mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 font-medium text-[15px] tracking-[-0.01em]">No program data available...</p>
          <button onClick={() => navigate('/my-lists')} className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[15px] font-medium transition-colors duration-200">Back to My Lists</button>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-[#fafafa] dark:bg-gray-900 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
        <div className="text-center space-y-6 px-6">
          <div className="w-12 h-12 border-2 border-black/20 dark:border-gray-600 border-t-black dark:border-t-white rounded-full animate-spin mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 font-medium text-[15px] tracking-[-0.01em]">Loading comparison...</p>
        </div>
      </div>
    );
  }

  const pieData = [
    { label: "Non-US IMGs", value: cleanPercentage(collegeData['%NON US IMGS'] || collegeData['% NON US IMGS'] || collegeData['%non-US IMG'] || collegeData['% non-US IMG'] || 0), color: "#404040" },
    { label: "US/Canadian", value: 100 - cleanPercentage(collegeData['%NON US IMGS'] || collegeData['% NON US IMGS'] || collegeData['%non-US IMG'] || collegeData['% non-US IMG'] || 0), color: "#e5e7eb" }
  ];

  const websiteUrl = extractWebsiteUrl(collegeData);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
      <div className="fixed inset-0 opacity-[0.02] dark:opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '64px 64px' }} />

      <div className="relative z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-black/[0.08] dark:border-gray-700 sticky top-0 z-20 transition-shadow duration-300">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => specialty ? navigate(`/table/${encodeURIComponent(specialty)}`) : navigate('/my-lists')} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors duration-200 group">
                <ChevronLeft size={20} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
                <span className="text-[15px] font-medium tracking-[-0.01em]">Back to {specialty || 'Lists'}</span>
              </button>
              <div className="h-5 w-px bg-black/10 dark:bg-gray-600" />
              <h1 className="text-xl font-semibold text-black dark:text-white tracking-[-0.02em]">Program Analysis</h1>
              {!hasCurrentSpecialtyAccess && (
                <div className="px-3 py-1.5 bg-black/[0.06] dark:bg-white/10 text-gray-600 dark:text-gray-400 text-[12px] rounded-full font-medium border border-black/8 dark:border-gray-600">Limited View</div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {!hasCurrentSpecialtyAccess && (
                <button onClick={() => setShowPaymentModal(true)} className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-5 py-2.5 rounded-full text-[14px] font-medium transition-all duration-200 flex items-center gap-2">
                  <Lock size={16} /> Unlock Full Analysis
                </button>
              )}
              <button onClick={toggleFavorite} disabled={isAddingFavorite} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[14px] font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${isFavorite ? 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200' : 'border border-black/15 dark:border-gray-600 hover:border-black/30 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                <Heart size={18} filled={isFavorite} className={isFavorite ? 'text-white dark:text-black' : ''} />
                <span>{isAddingFavorite ? 'Updating...' : isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-12 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-14">
          <div className={`lg:col-span-2 space-y-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-gray-700 rounded-2xl p-10 lg:p-12 shadow-sm hover:border-black/12 dark:hover:border-gray-600 transition-colors duration-200">
              <div className="flex items-center justify-between gap-8">
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl lg:text-3xl font-semibold text-black dark:text-white tracking-[-0.03em] leading-tight mb-4">
                    {displayProgramName}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-500 dark:text-gray-400 text-[15px] mb-4">
                    <span className="flex items-center gap-2"><MapPin size={18} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />{(collegeData['City'] || collegeData['CITY'] || '—')}, {(collegeData['State'] || collegeData['STATE'] || '—')}</span>
                    <span className="text-black/20 dark:text-gray-500">·</span>
                    <span className="flex items-center gap-2"><Building size={18} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />{collegeData['Type'] || collegeData['TYPE'] || '—'}</span>
                  </div>
                  {websiteUrl && (
                    <div className="flex items-center gap-2">
                      <Globe size={18} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium text-[14px] transition-colors duration-200 group">
                        <span>Visit Program Website</span>
                        <ExternalLink size={14} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                      </a>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  {hasCurrentSpecialtyAccess ? (
                    <>
                      <div className="text-4xl lg:text-5xl font-semibold text-black dark:text-white tracking-[-0.04em] mb-1">{animatedPercentage.toFixed(1)}%</div>
                      <div className="text-[12px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-widest">Compatibility</div>
                    </>
                  ) : (
                    <button onClick={() => setShowPaymentModal(true)} className="text-center group">
                      <div className="w-16 h-16 bg-black dark:bg-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-2 group-hover:scale-105 transition-transform duration-200">
                        <Lock size={24} className="text-white" />
                      </div>
                      <div className="text-[13px] text-gray-600 dark:text-gray-400 font-medium group-hover:text-black dark:group-hover:text-white transition-colors">Unlock Score</div>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-gray-700 rounded-2xl p-10 lg:p-12 shadow-sm hover:border-black/12 dark:hover:border-gray-600 transition-colors duration-200">
              <h3 className="text-[18px] font-semibold text-black dark:text-white tracking-[-0.02em] mb-8 flex items-center gap-2">
                <Building className="text-gray-500 dark:text-gray-400" size={22} /> Program Details
              </h3>
              <div className="space-y-0 divide-y divide-black/[0.06] dark:divide-gray-600">
                {[
                  { label: 'Program Name', icon: Building, keys: ['Program Name', 'PROGRAM NAME', 'NAME', 'Name'], isProgramName: true },
                  { label: 'State', icon: MapPin, keys: ['State', 'STATE'] },
                  { label: 'City', icon: MapPin, keys: ['City', 'CITY'] },
                  { label: 'Type', icon: Building, keys: ['Type', 'TYPE'] },
                  { label: 'Visa Requirements', icon: Users, keys: ['Visa', 'VISA', 'VISA REQUIREMENTS'] },
                  { label: '% Non-US IMGs', icon: Users, keys: ['%NON US IMGS', '% NON US IMGS', '%non-US IMG', '% non-US IMG', '%non us imgs', '% non us imgs'], premium: true },
                  { label: 'Program Director', icon: Users, keys: ['Program Director', 'PROGRAM DIRECTOR', 'PD'] },
                  { label: 'Program Coordinator', icon: Users, keys: ['Program Coordinator', 'PROGRAM COORDINATOR', 'PC'] },
                  { label: 'Positions Available', icon: Users, keys: ['POSITIONS', 'Positions', 'positions'] },
                  { label: 'Step 2 Cutoff', icon: TrendingUp, keys: ['INV STEP 2', 'STEP 2 CUTOFF', 'STEP 2', 'Step 2', 'step 2', 'STEP2', 'Step2'], premium: true },
                  { label: 'INV SS', icon: TrendingUp, keys: ['INV  SS', 'INV SS', 'Inv SS', 'INV_SS'] },
                  { label: 'INV GS', icon: TrendingUp, keys: ['INV  GS', 'INV GS', 'Inv GS', 'INV_GS'] },
                  { label: 'INV NS', icon: TrendingUp, keys: ['INV  NS', 'INV NS', 'Inv NS', 'INV_NS'] },
                  { label: 'Region', icon: MapPin, keys: ['REGION', 'Region', 'region'] },
                  { label: 'Website', icon: Globe, keys: ['WEBSITE', 'Website', 'RESIDENTS/PROGRAM WEBSITE', 'RESIDENTS/ PROGRAM WEBSITE', 'RESIDENTS', 'Residents', 'Additional Information', 'Additional info'], isWebsite: true },
                  { label: 'USCE Requirements', icon: Users, keys: ['USCE', 'Usce', 'usce', 'USCE REQUIREMENTS'] }
                ].map((field, index) => {
                  const Icon = field.icon;
                  const resolveValue = () => {
                    if (field.isProgramName) return displayProgramName === 'Program Name Not Available' ? '—' : displayProgramName;
                    for (const k of field.keys) {
                      const v = collegeData[k];
                      if (v != null && String(v).trim() && String(v).trim() !== '—') return String(v).trim();
                    }
                    return '—';
                  };
                  const value = resolveValue();
                  const isPremium = field.premium && !hasCurrentSpecialtyAccess;
                  return (
                    <div key={index} className="flex items-start justify-between gap-4 py-5 first:pt-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-xl flex-shrink-0 ${isPremium ? 'bg-black/[0.06] dark:bg-white/10' : 'bg-black/[0.06] dark:bg-white/10'}`}>
                          {isPremium ? <Lock size={16} className="text-gray-500 dark:text-gray-400" /> : <Icon size={16} className="text-gray-600 dark:text-gray-400" />}
                        </div>
                        <span className="text-[14px] font-medium text-gray-700 dark:text-gray-300">{field.label}</span>
                        {isPremium && <span className="px-2 py-0.5 bg-black/[0.06] dark:bg-white/10 text-gray-600 dark:text-gray-400 text-[11px] rounded-full font-medium uppercase tracking-wider flex-shrink-0">Premium</span>}
                      </div>
                      <div className="text-right max-w-[55%] flex-shrink-0">
                        {isPremium ? (
                          <button onClick={() => setShowPaymentModal(true)} className="text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white text-[13px] font-medium underline transition-colors">Unlock to view</button>
                        ) : field.isWebsite && value !== '—' ? (
                          <a href={value.includes('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white text-[13px] font-medium inline-flex items-center justify-end gap-1 transition-colors"><span>Visit Website</span><ExternalLink size={12} /></a>
                        ) : (
                          <span className="text-[14px] font-medium text-black dark:text-white break-words text-right">{value}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-gray-700 rounded-2xl p-10 lg:p-12 shadow-sm hover:border-black/12 dark:hover:border-gray-600 transition-colors duration-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[18px] font-semibold text-black dark:text-white tracking-[-0.02em] flex items-center gap-2"><Star className="text-gray-500 dark:text-gray-400" size={22} /> Overall Compatibility</h3>
                  {!hasCurrentSpecialtyAccess && <span className="px-2.5 py-1 bg-black/[0.06] dark:bg-white/10 text-gray-600 dark:text-gray-400 text-[11px] rounded-full font-medium uppercase tracking-wider flex items-center gap-1"><Lock size={10} /> Premium</span>}
                </div>
                <div className="flex justify-center"><RadialProgress percentage={animatedPercentage} size={250} isPaid={hasCurrentSpecialtyAccess} onUpgradeClick={() => setShowPaymentModal(true)} /></div>
                <div className="mt-6 text-center">
                  {hasCurrentSpecialtyAccess ? (
                    <p className="text-[14px] text-gray-500 dark:text-gray-400">Based on program statistics: <span className="font-semibold text-black dark:text-white">{displayPercentage(collegeData['%NON US IMGS'] || collegeData['% NON US IMGS'] || collegeData['%non-US IMG'] || collegeData['% non-US IMG'])}</span> IMG acceptance · <span className="font-semibold text-black dark:text-white">{collegeData['POSITIONS']}</span> positions</p>
                  ) : (
                    <button onClick={() => setShowPaymentModal(true)} className="text-[13px] text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium underline transition-colors">Unlock to see detailed compatibility analysis</button>
                  )}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-gray-700 rounded-2xl p-10 lg:p-12 shadow-sm hover:border-black/12 dark:hover:border-gray-600 transition-colors duration-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-[18px] font-semibold text-black dark:text-white tracking-[-0.02em] flex items-center gap-2"><Users className="text-gray-500 dark:text-gray-400" size={22} /> Program Composition</h3>
                  {!hasCurrentSpecialtyAccess && <span className="px-2.5 py-1 bg-black/[0.06] dark:bg-white/10 text-gray-600 dark:text-gray-400 text-[11px] rounded-full font-medium uppercase tracking-wider flex items-center gap-1"><Lock size={10} /> Premium</span>}
                </div>
                <div className="flex justify-center"><PieChart data={pieData} size={250} isPaid={hasCurrentSpecialtyAccess} onUpgradeClick={() => setShowPaymentModal(true)} /></div>
              </div>
            </div>

            {!hasCurrentSpecialtyAccess && (
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100/80 dark:from-gray-800 dark:to-gray-800/80 rounded-2xl p-10 lg:p-12 border border-black/[0.08] dark:border-gray-700">
                <div className="absolute top-0 right-0 w-40 h-40 bg-black/[0.03] dark:bg-white/5 rounded-full transform translate-x-20 -translate-y-20" />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-black dark:bg-gray-600 rounded-2xl flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></div>
                      <h3 className="text-[18px] font-semibold text-black dark:text-white tracking-[-0.02em]">Unlock Complete Program Analysis</h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-[14px] leading-relaxed mb-4 max-w-xl">Get access to compatibility scores, IMG composition data, Step 2 cutoffs, and strategic insights for better application planning.</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-black/40 dark:bg-white/40" /> Compatibility Scores</span>
                      <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-black/40 dark:bg-white/40" /> IMG Composition Data</span>
                      <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-black/40 dark:bg-white/40" /> Step 2 Requirements</span>
                    </div>
                  </div>
                  <button onClick={() => setShowPaymentModal(true)} className="bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[15px] font-medium transition-all duration-200 flex-shrink-0">Upgrade Now</button>
                </div>
              </div>
            )}

            <div className="text-center pt-4">
              <button onClick={() => specialty ? navigate(`/table/${encodeURIComponent(specialty)}`) : navigate('/my-lists')} className="inline-flex items-center gap-2 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-8 py-3.5 rounded-full text-[15px] font-medium transition-all duration-200 group">
                <ChevronLeft size={18} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
                Back to {specialty || 'My Lists'}
              </button>
            </div>
          </div>

          <div className={`lg:col-span-1 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="bg-white dark:bg-gray-800 border border-black/[0.08] dark:border-gray-700 rounded-2xl p-8 lg:p-10 shadow-sm sticky top-28 hover:border-black/12 dark:hover:border-gray-600 transition-colors duration-200">
              <h3 className="text-[18px] font-semibold text-black dark:text-white tracking-[-0.02em] mb-6 flex items-center gap-2"><Users className="text-gray-500 dark:text-gray-400" size={22} /> Your Profile</h3>
              <div className="space-y-3">
                {[
                  { label: 'Country', value: userProfile.country },
                  { label: '4-Year Aggregate', value: getAggregateMarksDisplay(userProfile), small: true },
                  { label: 'Graduation', value: userProfile.graduationTime },
                  { label: 'Step 1', value: userProfile.step1 },
                  { label: 'Step 2', value: userProfile.step2 },
                  { label: 'USCE', value: `${userProfile.usce} months` },
                  { label: 'Visa', value: userProfile.visa }
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-3 px-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100/80 dark:from-gray-700 dark:to-gray-700/80 border border-black/[0.06] dark:border-gray-600">
                    <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400">{row.label}:</span>
                    <span className={`font-semibold text-black dark:text-white text-right ${row.small ? 'text-[13px]' : 'text-[14px]'}`}>{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-5 bg-gradient-to-br from-gray-50 to-gray-100/60 dark:from-gray-700 dark:to-gray-700/60 rounded-2xl border border-black/[0.06] dark:border-gray-600">
                <h4 className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <TrendingUp size={16} className="text-gray-500 dark:text-gray-400" /> Program Quick Stats
                  {!hasCurrentSpecialtyAccess && <span className="px-2 py-0.5 bg-black/[0.06] dark:bg-white/10 text-gray-500 dark:text-gray-400 text-[10px] rounded-full uppercase tracking-wider">Premium</span>}
                </h4>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-semibold text-black dark:text-white tracking-[-0.02em]">
                      {hasCurrentSpecialtyAccess ? displayPercentage(collegeData['%NON US IMGS'] || collegeData['% NON US IMGS'] || collegeData['%non-US IMG'] || collegeData['% non-US IMG'] || '—') : (
                        <button onClick={() => setShowPaymentModal(true)} className="w-9 h-9 bg-black dark:bg-gray-600 rounded-xl flex items-center justify-center mx-auto hover:opacity-90 transition-opacity"><Lock size={16} className="text-white" /></button>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">IMG Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-black dark:text-white tracking-[-0.02em]">{collegeData['POSITIONS'] || collegeData['Positions'] || collegeData['positions'] || '—'}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Positions</div>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-5 bg-white dark:bg-gray-700/50 rounded-2xl border border-black/[0.06] dark:border-gray-600">
                <h4 className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><Building size={16} className="text-gray-500 dark:text-gray-400" /> Program Info</h4>
                <div className="space-y-2 text-[13px]">
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Location:</span><span className="font-medium text-black dark:text-white">{(collegeData['City'] || collegeData['CITY'] || '—')}, {(collegeData['State'] || collegeData['STATE'] || '—')}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600 dark:text-gray-400">Type:</span><span className="font-medium text-black dark:text-white">{collegeData['Type'] || collegeData['TYPE'] || '—'}</span></div>
                  {websiteUrl && (
                    <div className="pt-2">
                      <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium transition-colors"><Globe size={14} /><span>Visit Website</span><ExternalLink size={12} /></a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl border border-black/[0.08] dark:border-gray-700 animate-slide-up relative">
            <button onClick={() => setShowPaymentModal(false)} className="absolute top-5 right-5 text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-black dark:bg-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-6"><Lock className="w-8 h-8 text-white" /></div>
              <h3 className="text-xl font-semibold text-black dark:text-white tracking-[-0.02em] mb-2">Unlock {specialty} Analysis</h3>
              <p className="text-[14px] text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">Get access to compatibility scores, program composition charts, Step 2 CK cutoffs, and %Non-US IMG data for {specialty} programs.</p>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400">Payment Currency:</span>
                  <button onClick={() => setShowCurrencySelector(!showCurrencySelector)} className="text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white text-[13px] font-medium flex items-center gap-1 transition-colors">
                    Change Currency
                    <svg className={`w-4 h-4 transition-transform duration-200 ${showCurrencySelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 border border-black/[0.06] dark:border-gray-600 mb-3">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">{getPopularCountriesForPayment().find(c => c.country === selectedPaymentCountry)?.flag || "🌍"}</span>
                    <div className="text-center">
                      <div className="text-[15px] font-semibold text-black dark:text-white">{selectedPaymentCountry}</div>
                      <div className="text-[12px] text-gray-500 dark:text-gray-400">{getPricingInfo().currency}</div>
                    </div>
                  </div>
                </div>
                {showCurrencySelector && (
                  <div className="border border-black/[0.08] dark:border-gray-600 rounded-xl p-4 bg-gray-50/80 dark:bg-gray-700/80 max-h-60 overflow-y-auto">
                    <div className="text-[12px] font-medium text-gray-600 dark:text-gray-400 mb-3">Select currency:</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {getPopularCountriesForPayment().map((countryData) => (
                        <button key={countryData.country} onClick={() => { setSelectedPaymentCountry(countryData.country); setShowCurrencySelector(false); }} className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${selectedPaymentCountry === countryData.country ? 'bg-black/[0.08] dark:bg-white/10 border border-black/10 dark:border-gray-500 text-black dark:text-white' : 'hover:bg-white dark:hover:bg-gray-600 border border-transparent text-gray-700 dark:text-gray-300'}`}>
                          <span className="text-lg">{countryData.flag}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium truncate">{countryData.country}</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">{countryData.currency}</div>
                          </div>
                          <div className="text-[13px] font-medium text-gray-600 dark:text-gray-400">{formatCurrency(getPricingForCountrySync(countryData.country).amount, countryData.currency)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100/80 dark:from-gray-700 dark:to-gray-700/80 rounded-2xl p-6 mb-6 border border-black/[0.06] dark:border-gray-600">
                <div className="text-3xl font-semibold text-black dark:text-white tracking-[-0.03em] mb-1">{getPricingInfo().formattedAmount}</div>
                <div className="text-[13px] text-gray-500 dark:text-gray-400">One-time • Lifetime access to {specialty}</div>
                <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Equivalent to $10 USD • Live rates</div>
                {selectedPaymentCountry !== "India" && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Default: ₹{formatCurrency(getPricingForCountrySync("India").amount, "INR").replace('₹', '')} (India)</div>}
              </div>
              <div className="text-left mb-6 space-y-2.5">
                {['Compatibility scores for all programs', 'Program composition and IMG data', 'Step 2 CK cutoff requirements', 'Strategic application insights'].map((text, i) => (
                  <div key={i} className="flex items-center gap-3 text-[13px] text-gray-600 dark:text-gray-400">
                    <div className="w-4 h-4 rounded-full bg-black/10 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    {text}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowPaymentModal(false)} disabled={paymentLoading} className="flex-1 border border-black/15 dark:border-gray-600 hover:border-black/25 dark:hover:border-gray-500 text-gray-700 dark:text-gray-200 px-6 py-3 rounded-full text-[15px] font-medium transition-all duration-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Maybe Later</button>
                <button onClick={handlePayment} disabled={paymentLoading} className="flex-1 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black px-6 py-3 rounded-full text-[15px] font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">{paymentLoading ? "Processing..." : `Pay ${getPricingInfo().formattedAmount}`}</button>
              </div>
              <div className="mt-4 text-[11px] text-gray-400 dark:text-gray-500">All currencies converted from $10 USD using live exchange rates</div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}

export default ComparePage;
